/**
 * "La Experiencia" — Historia de Nelson
 * Ruta: /experiencia
 *
 * Experiencia inmersiva dramática sobre adicción.
 * 10 escenas: disclaimer → ringtone → chat WhatsApp simulado → plot twist → CTA.
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, PhoneOff, Volume2, VolumeX, X, Share2, ArrowRight } from "lucide-react";
import { Header } from "../components/Layout";

const SITE = "https://sinadicciones.org";

// ============================================================================
// GUIÓN — estructurado por escena
// ============================================================================

const CHAT_SCENES = [
  // SCENE 1 — Saludo y presentación
  {
    id: "saludo",
    label: "Mensaje 1 de 5",
    bgClass: "bg-neutral-950",
    messages: [
      { text: "Hola." },
      { text: "Soy Nelson." },
      { text: "¿Tienes 5 minutos? Quiero contarte mi historia." },
      { text: "Acá tengo una foto de cuando todo era simple.", image: "/assets/experiencia/nelson_cumpleanos.jpeg", imageCaption: "Mi cumpleaños. Tenía unos 5 años." },
      { text: "Quizás te ayuda. Quizás te ayuda a alguien que conoces." },
      { text: "Empecemos." },
    ],
  },
  // SCENE 2 — De amigos, de encajar
  {
    id: "de-amigos-encajar",
    label: "Mensaje 2 de 5",
    bgClass: "bg-gradient-to-br from-amber-950/40 via-neutral-950 to-neutral-950",
    messages: [
      { text: "Empezó como nada." },
      { text: "Era de amigos. De encajar. De ser parte del grupo." },
      { text: "Primero alcohol, los fines de semana." },
      { text: "Algunas pastillas para \u201Cpotenciar la fiesta\u201D." },
      { text: "Yo me reía. Yo encajaba. Yo era el alma." },
      { text: "Pensaba que tenía el control." },
    ],
  },
  // SCENE 3 — Cuando dejó de ser fin de semana
  {
    id: "dejo-de-ser-finde",
    label: "Mensaje 3 de 5",
    bgClass: "bg-gradient-to-br from-neutral-950 via-neutral-950 to-amber-950/30",
    messages: [
      { text: "Pero un día dejó de ser solo el fin de semana." },
      { text: "El alcohol se convirtió en lunes en la mañana." },
      { text: "Después llegó la cocaína. Después el tussi. Después la pasta base." },
      { text: "Sentí que la droga llenaba un vacío que nadie más podía llenar." },
      { text: "Empecé a pensar en consumir todo el día." },
      { text: "En el trabajo. Manejando. Despertándome." },
      { text: "Ya no era yo eligiendo." },
      { text: "Era mi cuerpo pidiéndomelo." },
    ],
  },
  // SCENE 4 — El descenso
  {
    id: "el-descenso",
    label: "Mensaje 4 de 5",
    bgClass: "bg-gradient-to-br from-neutral-950 via-neutral-950 to-red-950/30",
    messages: [
      { text: "Intenté parar muchas veces." },
      { text: "Cada vez juraba que era la última." },
      { text: "Me daba vergüenza pedir ayuda." },
      { text: "Pensaba: \u201Cyo puedo solo\u201D." },
      { text: "Pero no podía." },
      { text: "Empecé a alejarme de mi familia. De mi pareja. De mis amigos." },
      { text: "Mi mamá lloraba. Yo le mentía." },
      { text: "Le robaba. Le juraba que la quería." },
      { text: "Pisé la calle." },
      { text: "Dormí donde caía." },
    ],
  },
  // SCENE 5 — La frontera
  {
    id: "la-frontera",
    label: "Mensaje 5 de 5",
    bgClass: "bg-neutral-950",
    messages: [
      { text: "Y empezaron a llegar las ideas de suicidio." },
      { text: "Primero como un susurro." },
      { text: "Después como una voz." },
      { text: "Después como un plan." },
      { text: "Lo escribo y todavía duele." },
      { text: "Y al final, debo decir que…" },
    ],
  },
];

// ============================================================================
// Componentes de escena
// ============================================================================

function IntroVideoScreen({ onContinue }) {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [muted, setMutedLocal] = useState(true);
  const [ended, setEnded] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Autoplay silencioso (la mayoría de navegadores lo permite si está muted)
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
    const onEndedEv = () => setEnded(true);
    v.addEventListener("ended", onEndedEv);
    return () => {
      v.removeEventListener("ended", onEndedEv);
      try { v.pause(); } catch {}
    };
  }, []);

  function unmuteAndPlay() {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setMutedLocal(false);
    v.play().catch(() => {});
    setStarted(true);
  }

  function toggleMute() {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMutedLocal(v.muted);
    if (!v.muted) setStarted(true);
  }

  return (
    <motion.div
      className="fixed inset-0 bg-black z-[60] flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      <video
        ref={videoRef}
        src="/assets/experiencia/nelson_intro.mp4"
        className="absolute inset-0 w-full h-full object-cover"
        playsInline
        autoPlay
        muted
        preload="auto"
        data-testid="experience-intro-video"
      />

      {/* Gradient overlay para legibilidad */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80 pointer-events-none" />

      {/* Prompt de unmute si todavía está silenciado */}
      {!started && !ended && (
        <motion.button
          onClick={unmuteAndPlay}
          data-testid="experience-intro-unmute"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="absolute top-6 left-1/2 -translate-x-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white text-sm px-5 py-2.5 rounded-full flex items-center gap-2 transition"
        >
          <Volume2 className="w-4 h-4" />
          Toca para activar el sonido
        </motion.button>
      )}

      {/* Botón mute/unmute persistente */}
      <button
        onClick={toggleMute}
        data-testid="experience-intro-mute"
        aria-label={muted ? "Activar sonido" : "Silenciar"}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur text-white flex items-center justify-center border border-white/10 z-[2]"
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>

      {/* CTAs inferiores */}
      <div className="absolute bottom-8 left-0 right-0 flex flex-col items-center gap-3 px-6 z-[2]">
        <motion.button
          onClick={onContinue}
          data-testid="experience-intro-continue"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: ended ? 0.2 : 2.5, duration: 0.6 }}
          className="group w-full sm:w-auto px-10 py-4 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-full transition-all hover:scale-105 shadow-[0_0_60px_rgba(16,185,129,0.45)]"
        >
          Conocer la historia de Nelson
          <ArrowRight className="inline-block ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </motion.button>
        <motion.button
          onClick={onContinue}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.75 }}
          transition={{ delay: ended ? 0.4 : 3 }}
          className="text-neutral-300 hover:text-white text-xs underline underline-offset-4"
        >
          Saltar intro
        </motion.button>
      </div>
    </motion.div>
  );
}

function DisclaimerScreen({ onEnter, onExit }) {
  return (
    <motion.div
      className="fixed inset-0 bg-black z-50 flex items-center justify-center p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="max-w-lg text-center">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-8"
        >
          — UNA HISTORIA REAL —
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="text-4xl sm:text-5xl font-light text-white mb-6 leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          ¿Tienes 4 minutos <br />
          para conocer a Nelson?
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="text-neutral-400 text-sm mb-10"
        >
          Esto podría salvar una vida.<br />
          Tal vez la tuya. Tal vez la de alguien que amas.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="bg-amber-950/30 border border-amber-900/50 rounded-lg p-4 mb-4 text-left"
        >
          <p className="text-amber-200 text-xs leading-relaxed">
            <strong>⚠ Aviso:</strong> Esta experiencia aborda temas sensibles
            relacionados con adicción y salud mental. Si estás pasando por un momento vulnerable,
            puedes saltar la experiencia.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.85 }}
          className="bg-red-950/40 border border-red-900/50 rounded-lg p-4 mb-8 text-left"
          data-testid="disclaimer-helplines"
        >
          <p className="text-red-200 text-[11px] uppercase tracking-widest font-mono mb-2">
            Si necesitas ayuda <span className="text-red-300">ahora</span>
          </p>
          <div className="space-y-1.5 text-xs text-neutral-200 leading-relaxed">
            <p>
              <a href="tel:1412" className="text-emerald-300 hover:text-emerald-200 font-semibold underline underline-offset-2">SENDA · 1412</a>
              <span className="text-neutral-400"> — orientación en adicciones, gratis 24/7</span>
            </p>
            <p>
              <a href="tel:6003607777" className="text-emerald-300 hover:text-emerald-200 font-semibold underline underline-offset-2">Salud Responde · 600 360 7777</a>
              <span className="text-neutral-400"> — apoyo emocional y crisis suicida</span>
            </p>
            <p>
              <a href="tel:131" className="text-emerald-300 hover:text-emerald-200 font-semibold underline underline-offset-2">SAMU · 131</a>
              <span className="text-neutral-400"> — emergencia médica</span>
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="flex flex-col sm:flex-row gap-3 justify-center items-center"
        >
          <button
            onClick={onEnter}
            data-testid="experience-enter-btn"
            className="group relative w-full sm:w-auto px-10 py-4 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-full transition-all hover:scale-105 shadow-[0_0_40px_rgba(16,185,129,0.3)]"
          >
            Entrar a la experiencia
            <ArrowRight className="inline-block ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={onExit}
            data-testid="experience-skip-btn"
            className="text-neutral-500 hover:text-neutral-300 text-sm underline underline-offset-4"
          >
            Saltar al final
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.4 }}
          transition={{ delay: 2.4 }}
          className="text-neutral-500 text-xs mt-10"
        >
          🎧 Recomendamos audífonos
        </motion.p>
      </div>
    </motion.div>
  );
}

function RingtoneScreen({ onAnswer, onReject }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    tryPlay();
    const onVis = () => { if (document.visibilityState === "visible") tryPlay(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  return (
    <motion.div
      className="fixed inset-0 z-40 overflow-hidden flex flex-col items-center justify-between p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Background video */}
      <video
        ref={videoRef}
        src="/assets/experiencia/ringtone_bg.mp4"
        className="absolute inset-0 w-full h-full object-cover -z-10"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
        data-testid="ringtone-bg-video"
      />
      {/* Dark overlay for legibility */}
      <div className="pointer-events-none absolute inset-0 -z-[5] bg-black/60" />
      <div className="pointer-events-none absolute inset-0 -z-[5] bg-gradient-to-b from-black/70 via-black/40 to-black/85" />

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-center mt-16 relative z-10"
      >
        <p className="text-neutral-300 text-xs uppercase tracking-widest mb-2">Móvil</p>
        <p className="text-neutral-200 text-sm">Llamada entrante...</p>
      </motion.div>

      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: [1, 1.03, 1], opacity: 1 }}
        transition={{
          scale: { duration: 1.6, repeat: Infinity, ease: "easeInOut" },
          opacity: { duration: 0.6 },
        }}
        className="flex flex-col items-center relative z-10"
      >
        <div className="w-32 h-32 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-700 flex items-center justify-center text-5xl font-light text-black mb-6 shadow-[0_0_80px_rgba(16,185,129,0.5)] ring-4 ring-black/40">
          N
        </div>
        <p className="text-white text-3xl font-light mb-1 drop-shadow-lg">Nelson</p>
        <p className="text-neutral-300 text-sm drop-shadow">+56 9 7891 2345</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="flex gap-12 mb-16 relative z-10"
      >
        <button
          onClick={onReject}
          data-testid="ringtone-reject-btn"
          aria-label="Rechazar"
          className="group flex flex-col items-center gap-3"
        >
          <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center group-hover:bg-red-500 transition-colors shadow-[0_0_30px_rgba(220,38,38,0.5)]">
            <PhoneOff className="w-7 h-7 text-white" />
          </div>
          <span className="text-neutral-200 text-xs drop-shadow">Rechazar</span>
        </button>
        <button
          onClick={onAnswer}
          data-testid="ringtone-answer-btn"
          aria-label="Contestar"
          className="group flex flex-col items-center gap-3"
        >
          <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center group-hover:bg-emerald-400 transition-colors shadow-[0_0_30px_rgba(16,185,129,0.5)] animate-pulse">
            <Phone className="w-7 h-7 text-black" />
          </div>
          <span className="text-neutral-200 text-xs drop-shadow">Contestar</span>
        </button>
      </motion.div>
    </motion.div>
  );
}

function ChatScene({ sceneIndex, scene, onNext }) {
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [typing, setTyping] = useState(true);
  const chatRef = useRef(null);
  const endRef = useRef(null);
  const runIdRef = useRef(0);

  useEffect(() => {
    // Incrementar runId para invalidar ejecuciones previas (fix StrictMode double mount)
    runIdRef.current += 1;
    const myRun = runIdRef.current;
    setVisibleMessages([]);
    setTyping(true);

    const schedule = async () => {
      for (let i = 0; i < scene.messages.length; i++) {
        if (runIdRef.current !== myRun) return; // cancelado por remount
        const msg = scene.messages[i];
        setTyping(true);
        // Tiempo de "typing" más humano: proporcional al largo del texto
        const typingMs = Math.max(
          900,
          Math.min(2400, (msg.text || "").length * 35 + (msg.image ? 700 : 0))
        );
        await sleep(typingMs);
        if (runIdRef.current !== myRun) return;
        // Transición suave: pausa corta entre "escribiendo" y aparición del mensaje
        setTyping(false);
        await sleep(180);
        if (runIdRef.current !== myRun) return;
        setVisibleMessages((prev) => (prev.length > i ? prev : [...prev, msg]));
        // Pausa de lectura entre mensajes
        const readMs = Math.max(
          1100,
          Math.min(2600, (msg.text || "").length * 38 + (msg.image ? 1400 : 0))
        );
        await sleep(readMs);
      }
      if (runIdRef.current !== myRun) return;
      setTyping(false);
      await sleep(1800);
      if (runIdRef.current === myRun) onNext();
    };

    schedule();
    return () => {
      runIdRef.current += 1; // invalida el loop actual
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneIndex]);

  useEffect(() => {
    if (endRef.current) {
      endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    } else if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [visibleMessages, typing]);

  return (
    <motion.div
      key={sceneIndex}
      className={`fixed inset-0 z-30 flex flex-col ${scene.bgClass}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {/* Header tipo WhatsApp */}
      <div className="bg-emerald-900/40 backdrop-blur-md border-b border-emerald-900/50 px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-700 flex items-center justify-center text-xl font-light text-black">
          N
        </div>
        <div className="flex-1">
          <p className="text-white text-base font-medium">Nelson</p>
          <p className="text-emerald-400 text-xs">{typing ? "escribiendo..." : "en línea"}</p>
        </div>
        <span className="text-neutral-400 text-[10px] uppercase tracking-wider hidden sm:block">{scene.label}</span>
      </div>

      {/* Chat body */}
      <div ref={chatRef} className="flex-1 overflow-y-auto px-4 py-6 space-y-2">
        <AnimatePresence initial={false} mode="popLayout">
          {visibleMessages.map((m, i) => (
            <motion.div
              layout
              key={`${sceneIndex}-${i}`}
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 320,
                damping: 26,
                mass: 0.7,
              }}
              className="flex"
            >
              <div className={`bg-neutral-800 text-white rounded-2xl rounded-bl-sm shadow-md max-w-[85%] sm:max-w-[70%] ${m.image ? "p-1.5" : "px-4 py-2.5 text-[15px]"}`}>
                {m.image && (
                  <div className="overflow-hidden rounded-xl mb-1">
                    <img
                      src={m.image}
                      alt={m.imageCaption || "Foto de Nelson"}
                      loading="lazy"
                      decoding="async"
                      className="block w-full max-w-[260px] h-auto object-cover"
                    />
                  </div>
                )}
                {m.text && <p className={m.image ? "text-[14px] text-neutral-200 px-2 pt-1 pb-1" : ""}>{m.text}</p>}
                {m.imageCaption && (
                  <p className="text-[11px] text-neutral-500 px-2 pb-1.5 italic">{m.imageCaption}</p>
                )}
              </div>
            </motion.div>
          ))}
          {typing && (
            <motion.div
              layout
              key={`typing-${sceneIndex}`}
              initial={{ opacity: 0, y: 6, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92, transition: { duration: 0.18, ease: "easeOut" } }}
              transition={{
                type: "spring",
                stiffness: 340,
                damping: 28,
                mass: 0.6,
              }}
              className="flex"
            >
              <div className="bg-neutral-800 rounded-2xl rounded-bl-sm px-4 py-3 shadow-md">
                <div className="flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className="w-2 h-2 bg-neutral-400 rounded-full"
                      animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
                      transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.18, ease: "easeInOut" }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div ref={endRef} style={{ height: 1 }} />
      </div>
    </motion.div>
  );
}

function DeathReveal({ onNext }) {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 2000);
    const t2 = setTimeout(() => setStage(2), 3400);   // "Nelson se suicidó"
    const t3 = setTimeout(() => setStage(3), 6200);   // "en mayo de 2024"
    const t4 = setTimeout(() => setStage(4), 9000);   // "Tenía solo 34 años"
    const t5 = setTimeout(() => setStage(5), 12500);  // estadísticas
    const t6 = setTimeout(() => setStage(6), 18500);  // "Si te identificas..."
    const t7 = setTimeout(() => setStage(7), 22000);  // botón continuar
    return () => { [t1,t2,t3,t4,t5,t6,t7].forEach(clearTimeout); };
  }, []);

  return (
    <motion.div
      className="fixed inset-0 z-40 bg-black flex flex-col items-center justify-center p-6 sm:p-8 overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.8 }}
    >
      <div className="max-w-2xl w-full text-center py-8">
        {stage >= 2 && (
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 2.4, ease: "easeOut" }}
            className="text-white text-3xl sm:text-5xl font-light leading-relaxed"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Nelson se suicidó
          </motion.p>
        )}
        {stage >= 3 && (
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 2.4, ease: "easeOut" }}
            className="text-white text-3xl sm:text-5xl font-light leading-relaxed mt-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            en mayo de 2024.
          </motion.p>
        )}
        {stage >= 4 && (
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 2.4, ease: "easeOut" }}
            className="text-neutral-400 text-lg sm:text-2xl mt-10 font-light"
          >
            Tenía solo 34 años.
          </motion.p>
        )}

        {/* Estadísticas */}
        {stage >= 5 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2.0 }}
            className="mt-14 sm:mt-16 space-y-4 sm:space-y-5 text-left max-w-xl mx-auto"
            data-testid="death-stats"
          >
            <p className="text-neutral-500 text-[10px] uppercase tracking-[0.3em] text-center mb-6 font-mono">
              &gt; La realidad en Chile
            </p>
            <div className="border-l-2 border-red-500/60 pl-4 sm:pl-5">
              <p className="text-neutral-200 text-base sm:text-lg leading-relaxed">
                <span className="text-red-400 font-semibold">1 de cada 4</span> personas con adicción severa intenta suicidarse al menos una vez.
              </p>
            </div>
            <div className="border-l-2 border-red-500/60 pl-4 sm:pl-5">
              <p className="text-neutral-200 text-base sm:text-lg leading-relaxed">
                El <span className="text-red-400 font-semibold">70%</span> de los suicidios en hombres de 25-45 años están relacionados con consumo de sustancias.
              </p>
            </div>
            <div className="border-l-2 border-red-500/60 pl-4 sm:pl-5">
              <p className="text-neutral-200 text-base sm:text-lg leading-relaxed">
                Cada día mueren entre <span className="text-red-400 font-semibold">5 y 7 personas</span> en Chile por causas relacionadas a la adicción.
              </p>
            </div>
          </motion.div>
        )}

        {/* Mensaje final + invitación */}
        {stage >= 6 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.8 }}
            className="mt-14"
          >
            <p
              className="text-white text-2xl sm:text-3xl font-light leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Si te identificas,<br />pide ayuda.
            </p>
            <p className="text-emerald-400 text-base sm:text-lg mt-3">
              Hay salida.
            </p>

            {/* Líneas de ayuda — disponibles 24/7 */}
            <div className="mt-8 max-w-md mx-auto bg-red-950/30 border border-red-900/40 rounded-lg p-4 text-left" data-testid="death-helplines">
              <p className="text-red-200 text-[11px] uppercase tracking-widest font-mono mb-2 text-center">
                Si necesitas hablar con alguien <span className="text-red-300">ahora</span>
              </p>
              <div className="space-y-1.5 text-xs sm:text-sm text-neutral-200 leading-relaxed">
                <p>
                  <a href="tel:1412" className="text-emerald-300 hover:text-emerald-200 font-semibold underline underline-offset-2">SENDA · 1412</a>
                  <span className="text-neutral-400"> — orientación en adicciones, 24/7</span>
                </p>
                <p>
                  <a href="tel:6003607777" className="text-emerald-300 hover:text-emerald-200 font-semibold underline underline-offset-2">Salud Responde · 600 360 7777</a>
                  <span className="text-neutral-400"> — crisis suicida y apoyo emocional</span>
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {stage >= 7 && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
            onClick={onNext}
            data-testid="death-continue-btn"
            className="mt-12 text-neutral-400 hover:text-white text-sm underline underline-offset-8 transition-colors"
          >
            Continuar
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}

function Epilogue({ onNext }) {
  return (
    <motion.div
      className="fixed inset-0 z-40 bg-gradient-to-b from-neutral-950 via-neutral-900 to-emerald-950/40 flex items-center justify-center p-8 overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5 }}
    >
      <div className="max-w-xl text-center py-10">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 1.2 }}
          className="text-emerald-400 text-xs uppercase tracking-[0.3em] mb-6"
        >
          — Esto no es ficción —
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1.2 }}
          className="text-white text-2xl sm:text-3xl font-light leading-relaxed mb-10"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Esta es una historia real.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3, duration: 1 }}
          className="space-y-5 text-neutral-300 text-base sm:text-lg leading-relaxed"
        >
          <p>Una de miles que ocurren en Chile cada año.</p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 4.4, duration: 1 }}
            className="pt-2"
          >
            Nelson no era un &ldquo;delincuente&rdquo;.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 5.8, duration: 1 }}
            className="text-white text-xl sm:text-2xl font-light"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Era un hijo, un padre, un hermano…
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 7.4, duration: 1 }}
            className="pt-1"
          >
            que empezó consumiendo para sentirse vivo
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 9, duration: 1 }}
            className="text-neutral-100"
          >
            y terminó muriendo para dejar de sentir.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 11.2, duration: 1 }}
            className="pt-6 text-neutral-400"
          >
            La diferencia entre Nelson<br />
            y las miles de personas que hoy están recuperadas,<br />
            <span className="text-emerald-400">es que ellas pidieron ayuda a tiempo.</span>
          </motion.p>
        </motion.div>

        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 14, duration: 0.8 }}
          onClick={onNext}
          data-testid="epilogue-continue-btn"
          className="mt-12 text-neutral-400 hover:text-white text-sm underline underline-offset-8"
        >
          Continuar →
        </motion.button>
      </div>
    </motion.div>
  );
}

function FinalCTA({ onAskHelp, onShare }) {
  return (
    <motion.div
      className="fixed inset-0 z-40 bg-gradient-to-b from-emerald-950 via-neutral-950 to-black overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.2 }}
    >
      <Header />
      <div className="min-h-screen flex items-center justify-center p-6 pt-24">
      <div className="max-w-2xl text-center py-10">
        <motion.h2
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="text-4xl sm:text-6xl font-light text-white mb-8 leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          A ti todavía<br className="sm:hidden" /> te queda tiempo.
        </motion.h2>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="space-y-3 text-neutral-300 text-lg sm:text-xl mb-10"
        >
          <p>No esperes a que tu familia se enferme.</p>
          <p>No esperes a perderlo todo.</p>
          <p className="pt-3 text-white text-xl sm:text-2xl font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Pide ayuda hoy.
          </p>
          <p className="text-emerald-400 font-medium">Es el acto más valiente que puedes hacer.</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.4, duration: 0.8 }}
          className="space-y-4"
        >
          <button
            onClick={onAskHelp}
            data-testid="experience-help-btn"
            className="w-full sm:w-auto px-12 py-5 bg-emerald-500 hover:bg-emerald-400 text-black text-lg font-semibold rounded-full transition-all hover:scale-105 shadow-[0_0_60px_rgba(16,185,129,0.4)]"
          >
            Pedir ayuda ahora — Cotizar gratis
          </button>

          <div className="pt-4">
            <a
              href="tel:1412"
              data-testid="experience-senda-btn"
              className="inline-block text-neutral-300 hover:text-emerald-400 text-sm underline underline-offset-4"
            >
              ☎ Llamar al 1412 (SENDA · 24 horas · gratis)
            </a>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3.6, duration: 1 }}
            className="pt-6 text-white text-lg sm:text-xl font-light"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            No estás solo.
          </motion.p>

          <div className="pt-2">
            <button
              onClick={onShare}
              data-testid="experience-share-btn"
              className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-300 text-sm transition-colors"
            >
              <Share2 className="w-4 h-4" /> Compartir esta historia
            </button>
          </div>
        </motion.div>
      </div>
      </div>
    </motion.div>
  );
}

// ============================================================================
// Página principal
// ============================================================================

export default function ExperienciaNelson() {
  const navigate = useNavigate();
  const [step, setStep] = useState(-1); // -1=intro video, 0=disclaimer, 1=ringtone, 2=chat (5 escenas vía chatIdx 0-4), 6=deathReveal+stats, 7=epilogue, 8=CTA
  const [chatIdx, setChatIdx] = useState(0);

  // Canonical + SEO
  const canonical = `${SITE}/experiencia`;

  function handleEnter() {
    setStep(1);
    import('../lib/track').then(m => m.trackEvent('experiencia_nelson_started', '/experiencia'));
  }
  function handleSkipToEnd() {
    setStep(8);
    import('../lib/track').then(m => m.trackEvent('experiencia_nelson_completed', '/experiencia'));
  }
  function handleAnswer() {
    setStep(2);
  }
  function handleReject() {
    // Mini pantalla de "Nelson ya no va a llamar nunca más" → después al chat
    setStep(1.5);
  }
  function handleChatNext() {
    if (chatIdx < CHAT_SCENES.length - 1) {
      setChatIdx(chatIdx + 1);
    } else {
      setStep(6);
      import('../lib/track').then(m => m.trackEvent('experiencia_nelson_completed', '/experiencia'));
    }
  }
  function handleDeathNext() {
    setStep(7);
  }
  function handleEpilogueNext() {
    setStep(8);
  }
  function handleAskHelp() {
    navigate("/cotizar?source=experiencia-nelson");
  }
  async function handleShare() {
    const shareData = {
      title: "La Historia de Nelson",
      text: "Esta historia salvó mi forma de ver las adicciones.",
      url: canonical,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(canonical);
        alert("Link copiado al portapapeles");
      }
    } catch {}
  }

  const currentScene = CHAT_SCENES[chatIdx];
  const progressPct = useMemo(() => {
    if (step === 0) return 0;
    if (step === 1 || step === 1.5) return 8;
    if (step >= 2 && step <= 5) return 15 + chatIdx * 20;
    if (step === 6) return 85;
    if (step === 7) return 92;
    return 100;
  }, [step, chatIdx]);

  return (
    <>
      
        <title>La Historia de Nelson - Una experiencia sobre adicción | SinAdicciones.org</title>
        <meta
          name="description"
          content="Una experiencia inmersiva de 4 minutos sobre la cruda realidad de la adicción en Chile. Conoce a Nelson. Tal vez salves una vida."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content="¿Tienes 4 minutos para conocer a Nelson? | SinAdicciones.org" />
        <meta property="og:description" content="Una historia que podría salvar una vida. Una experiencia inmersiva sobre la adicción en Chile." />
        <meta property="og:image" content={`${SITE}/og-experiencia-nelson.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="¿Tienes 4 minutos para conocer a Nelson?" />
        <meta name="twitter:description" content="Una historia que podría salvar una vida." />
        <meta name="twitter:image" content={`${SITE}/og-experiencia-nelson.jpg`} />
        <link
          href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@300;400;500;600&display=swap"
          rel="stylesheet"
        />
      

      <div className="fixed inset-0 bg-black" data-testid="experience-root">
        {/* Progress bar */}
        {step > 0 && step < 8 && (
          <div className="fixed top-0 left-0 right-0 h-1 bg-neutral-900 z-[60]">
            <motion.div
              className="h-full bg-emerald-500"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.6 }}
            />
          </div>
        )}

        {/* Top controls */}
        {step > 0 && step < 8 && (
          <div className="fixed top-3 right-3 z-[55] flex gap-2">
            <button
              onClick={handleSkipToEnd}
              data-testid="experience-close-btn"
              aria-label="Cerrar"
              className="w-9 h-9 rounded-full bg-neutral-900/80 backdrop-blur text-neutral-300 hover:text-white flex items-center justify-center border border-neutral-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === -1 && <IntroVideoScreen key="intro-video" onContinue={() => setStep(0)} />}
          {step === 0 && <DisclaimerScreen key="disclaimer" onEnter={handleEnter} onExit={handleSkipToEnd} />}
          {step === 1 && <RingtoneScreen key="ringtone" onAnswer={handleAnswer} onReject={handleReject} />}
          {step === 1.5 && <RejectedScreen key="rejected" onContinue={() => setStep(2)} />}
          {step >= 2 && step <= 5 && currentScene && (
            <ChatScene
              key={`chat-${chatIdx}`}
              sceneIndex={chatIdx}
              scene={currentScene}
              onNext={handleChatNext}
            />
          )}
          {step === 6 && <DeathReveal key="death" onNext={handleDeathNext} />}
          {step === 7 && <Epilogue key="epilogue" onNext={handleEpilogueNext} />}
          {step === 8 && <FinalCTA key="cta" onAskHelp={handleAskHelp} onShare={handleShare} />}
        </AnimatePresence>
      </div>
    </>
  );
}

// Transition en inicio cuando rechazan la llamada
function RejectedScreen({ onContinue }) {
  useEffect(() => {
    const t = setTimeout(onContinue, 4800);
    return () => clearTimeout(t);
  }, [onContinue]);
  return (
    <motion.div
      className="fixed inset-0 z-40 bg-black flex items-center justify-center p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
    >
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 1.2 }}
        className="text-neutral-300 text-xl sm:text-2xl font-light text-center max-w-md"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        Está bien.
        <br />
        <span className="text-neutral-500 text-base">
          Pero Nelson ya no va a llamar nunca más.
        </span>
        <br />
        <span className="text-neutral-600 text-xs mt-4 block">
          Alcanzó a escribirte unos mensajes antes...
        </span>
      </motion.p>
    </motion.div>
  );
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

// Cambiar el step para saltarlo si el usuario rechaza y luego entra igual
// (la lógica está en handleReject -> step 1.5, que después continúa al chat)
